import { ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  Building2,
  Calendar,
  CreditCard,
  Shield,
  Stethoscope,
  UserIcon,
} from 'lucide-react';
import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { RainmakerLogo } from '@/components/rainmaker';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useUpdateTaskMutation } from '@/services/api';
import { Task } from '@/services/types/client-intake-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskStatus,
} from '@/services/types/task-types';
import { createConsistentDate } from '@/utils/date';
import { highlightText } from '@/utils/searchUtils';
import {
  formatTaskDueDate,
  formatTaskDueDateCompact,
  isTaskPastDue,
  isTaskUrgent,
} from '@/utils/taskDateUtils';

import { SnoozeButton } from './SnoozeButton';
import { TaskStatusDisplay } from './TaskStatusDisplay';

const getTaskPriority = (task: Task): number => {
  // Higher number = higher priority
  if (isTaskPastDue(task)) return 100;
  if (isTaskUrgent(task)) return 90;
  if (task.status.canonical_status === CanonicalTaskStatus.BLOCKED) return 80;
  if (task.status.canonical_status === CanonicalTaskStatus.IN_PROGRESS)
    return 70;
  if (task.status.canonical_status === CanonicalTaskStatus.PENDING) return 60;
  return 0;
};

const getPriorityGroupColor = (groupName: string) => {
  switch (groupName) {
    case 'Immediate':
      return {
        border: 'border-l-red-500',
        bg: 'bg-zinc-100',
        indicator: 'bg-red-500',
      };
    case 'Overdue':
      return {
        border: 'border-l-red-400',
        bg: 'bg-zinc-100',
        indicator: 'bg-red-400',
      };
    case 'Next Action Within 24 Hours':
      return {
        border: 'border-l-orange-500',
        bg: 'bg-zinc-100',
        indicator: 'bg-orange-500',
      };
    case 'Next Action Within 5 Days':
      return {
        border: 'border-l-amber-500',
        bg: 'bg-zinc-100',
        indicator: 'bg-amber-500',
      };
    case 'Next Action Within 2 Weeks':
      return {
        border: 'border-l-blue-500',
        bg: 'bg-zinc-100',
        indicator: 'bg-blue-500',
      };
    case 'Next Action Beyond 2 Weeks':
      return {
        border: 'border-l-zinc-400',
        bg: 'bg-zinc-100',
        indicator: 'bg-zinc-400',
      };
    case 'Completed':
      return {
        border: 'border-l-green-500',
        bg: 'bg-zinc-100',
        indicator: 'bg-green-500',
      };
    default:
      return {
        border: 'border-l-zinc-300',
        bg: 'bg-zinc-100',
        indicator: 'bg-zinc-300',
      };
  }
};

type CompactTaskGroupProps = {
  name: string;
  tasks: Task[];
  isOpen: boolean;
  onToggle: () => void;
  matterId: string;
  showUrgentOnly?: boolean;
  urgentTaskIds?: Set<string>;
  searchTerm?: string;
};

export const CompactTaskGroup = ({
  name,
  tasks,
  isOpen,
  onToggle,
  matterId,
  showUrgentOnly = false,
  urgentTaskIds = new Set(),
  searchTerm,
}: CompactTaskGroupProps) => {
  const sortedTasks = React.useMemo(() => {
    // For priority-based grouping, we don't need to filter out urgent tasks
    // since they're already grouped by priority
    return [...tasks].sort((a, b) => {
      return getTaskPriority(b) - getTaskPriority(a);
    });
  }, [tasks]);

  const [updateTask] = useUpdateTaskMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle task selection via query parameters
  const handleTaskClick = (taskId: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('task_id', taskId);
    setSearchParams(newSearchParams);
  };

  if (sortedTasks.length === 0) return null;

  // Calculate counts
  const completedCount = tasks.filter(
    (task) => task.status.canonical_status === CanonicalTaskStatus.COMPLETED,
  ).length;
  const totalCount = tasks.length;
  const remainingCount = totalCount - completedCount;

  // Get priority-specific styling
  const colors = getPriorityGroupColor(name);
  const isHighPriority =
    name === 'Immediate' ||
    name === 'Overdue' ||
    name === 'Next Action Within 24 Hours';

  return (
    <div className="border-b border-zinc-100 last:border-none">
      {/* Group Header */}
      <button
        type="button"
        className={cn(
          'flex items-center justify-between w-full px-3 py-1.5 text-left focus:outline-none transition-colors',
          colors.border,
          colors.bg,
          'border-l-3',
        )}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <ChevronRightIcon
            className={cn(
              'h-2.5 w-2.5 text-zinc-400 transition-transform shrink-0',
              isOpen && 'rotate-90',
            )}
          />
          <span className="text-xs font-semibold text-zinc-800 truncate">
            {name}
          </span>
        </div>
        <span className="text-[10px] font-medium text-zinc-500 tabular-nums">
          {totalCount}
        </span>
      </button>

      {/* Task List */}
      {isOpen && (
        <div className="divide-y divide-zinc-100">
          {sortedTasks.map((task: Task) => {
            const isPastDue = isTaskPastDue(task);
            const isInsuranceTask = [
              CanonicalTaskGroup.FIRST_PARTY_INSURANCE,
              CanonicalTaskGroup.THIRD_PARTY_INSURANCE,
            ].includes(task.group.canonical_group);
            const isRainmakerCompleted =
              task.has_rainmaker &&
              task.status.canonical_status === CanonicalTaskStatus.COMPLETED;

            return (
              <div
                key={task.id}
                onClick={() => handleTaskClick(task.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTaskClick(task.id);
                  }
                }}
                className="relative px-3 py-2 bg-white group transition-colors hover:bg-zinc-50 cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`Open task: ${task.name}`}
              >
                {/* Row 1: Task name + Status */}
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    {task.has_rainmaker && (
                      <RainmakerLogo
                        size={12}
                        className="text-violet-500 shrink-0"
                      />
                    )}
                    <span className="text-xs font-semibold text-zinc-800 truncate group-hover:text-zinc-600 group-hover:underline">
                      {searchTerm
                        ? highlightText(task.name, searchTerm)
                        : task.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {task.due_date && (
                      <SnoozeButton
                        currentDueDate={task.due_date}
                        onDueDateChange={async (date) => {
                          if (date) {
                            try {
                              await updateTask({
                                matterId,
                                taskId: task.id,
                                task: { due_date: createConsistentDate(date) },
                              }).unwrap();
                            } catch (error) {
                              console.error('Failed to update task:', error);
                            }
                          }
                        }}
                        size="sm"
                        className="h-3 w-3 opacity-0 group-hover:opacity-100"
                      />
                    )}
                    <TaskStatusDisplay
                      status={task.status.canonical_status}
                      compact
                    />
                  </div>
                </div>

                {/* Row 2: Metadata */}
                <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                  {task.coverage?.carrier?.name && (
                    <div className="flex items-center gap-1 max-w-[100px]">
                      <CreditCard className="h-2.5 w-2.5 text-blue-500 shrink-0" />
                      <span className="font-medium text-zinc-600 truncate">
                        {task.coverage.carrier.name}
                      </span>
                    </div>
                  )}
                  {task.medical_note?.name && (
                    <div className="flex items-center gap-1 max-w-[100px]">
                      <Stethoscope className="h-2.5 w-2.5 text-green-500 shrink-0" />
                      <span className="font-medium text-zinc-600 truncate">
                        {task.medical_note.name}
                      </span>
                    </div>
                  )}
                  {task.police_report && (
                    <div className="flex items-center gap-1 max-w-[100px]">
                      <Shield className="h-2.5 w-2.5 text-purple-500 shrink-0" />
                      <span className="font-medium text-zinc-600 truncate">
                        {task.police_report.report_identifier ||
                          'Police Report'}
                      </span>
                    </div>
                  )}
                  {task.due_date && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-2.5 w-2.5 text-zinc-400" />
                          <span
                            className={cn(
                              'font-medium',
                              isPastDue && 'text-red-600',
                            )}
                          >
                            {formatTaskDueDateCompact(task.due_date)}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        {formatTaskDueDate(task.due_date)}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {task.assignee && (
                    <div className="flex items-center gap-1">
                      <UserIcon className="h-2.5 w-2.5 text-zinc-400" />
                      <span className="font-medium">
                        {task.assignee.first_name?.[0]}
                        {task.assignee.last_name?.[0]}
                      </span>
                    </div>
                  )}
                  {isInsuranceTask && (
                    <div className="flex items-center gap-1 opacity-60">
                      <Building2 className="h-2.5 w-2.5 text-zinc-400" />
                      <span>{task.group.name}</span>
                    </div>
                  )}
                </div>

                {/* Rainmaker watermark */}
                {isRainmakerCompleted && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-5">
                    <RainmakerLogo
                      size={24}
                      className="text-emerald-500 -rotate-12"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
